import { useEffect, useMemo, useRef, useState } from 'react'

const paginateList = <T = unknown>(records: T[], page: number, limit: number) => {
  return records.slice((page - 1) * limit, (page - 1) * limit + limit)
}

// `getPaginatedList` clamps to the last valid page automatically — fixes the
// "deleted last row on page 2 → table is empty even though page 1 has data"
// shape callers used to have to patch themselves with an extra effect. The
// observed total is captured into a ref (safe to mutate during render — refs
// don't trigger re-renders), and the clamp runs in a post-commit effect.
// Earlier implementations called setState inside getPaginatedList itself,
// which is a render-phase state update — React allows it with a guard but
// it's flagged by linters and produces an extra render. Using a ref avoids
// the issue entirely.
type UsePopupHandlersAttributes = {
  limit: number
}
export const useGetPagingHelpers = <T = unknown>(
  { limit }: UsePopupHandlersAttributes = { limit: 10 },
  pageOutside?: number,
  setPageOutside?: (page: number) => void,
) => {
  const [page, setPage] = useState(1)
  const currentPage = pageOutside || page
  const setCurrentPage = setPageOutside || setPage
  // Records the total length passed to ``getPaginatedList`` on the most
  // recent render. Mutating a ref during render is allowed because refs
  // don't subscribe components to re-render.
  const lastSeenTotalRef = useRef<number | null>(null)

  // Runs after every commit. Reads the latest ref (set by whichever
  // ``getPaginatedList`` call ran in this render) and clamps the page
  // when it's out of the current range. The lack of a deps array is
  // intentional — we don't know which render mutated the ref, so we
  // check after every commit. The clamp only schedules a setState
  // when the page is genuinely out of bounds, so this doesn't loop.
  useEffect(() => {
    const total = lastSeenTotalRef.current
    if (total === null) return
    const lastValidPage = Math.max(1, Math.ceil(total / limit))
    if (currentPage > lastValidPage) {
      setCurrentPage(lastValidPage)
    }
  })

  return useMemo(
    () => ({
      handlePageChange: setCurrentPage,
      getPaginatedList: (records: T[]) => {
        // Pure ref write — does NOT trigger a re-render. The clamp
        // effect above reads this after commit.
        lastSeenTotalRef.current = records.length
        return paginateList(records, currentPage, limit)
      },
      page: currentPage,
      limit,
    }),
    [currentPage, limit, setCurrentPage],
  )
}

export default useGetPagingHelpers
